import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { MessageSquare, Send, ThumbsUp, ThumbsDown, BookOpen, Sparkles, Plus, X, ShieldCheck, FileText, Clock, Trash2 } from "lucide-react";

function MessageBubble({ message, onFeedback, onVerify, canVerify }) {
  const { t } = useI18n();
  const [fbComment, setFbComment] = useState("");
  const [showFB, setShowFB] = useState(false);

  if (message.role === "user") {
    return (
      <div className="flex justify-end mb-3" data-testid="msg-user">
        <div className="max-w-[80%] bg-[#475569] text-white rounded-[16px] rounded-tr-sm px-4 py-2.5">
          <div className="text-[13px] whitespace-pre-wrap">{message.content}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start mb-3" data-testid="msg-assistant">
      <div className="max-w-[85%]">
        <div className="bg-white border border-[#eeeded] rounded-[16px] rounded-tl-sm px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider text-[#475569] font-semibold">
            <Sparkles size={11} /> Sage{message.verified && <ShieldCheck size={11} className="text-[#008932]" />}
          </div>
          <div className="text-[13px] text-[#323232] whitespace-pre-wrap leading-relaxed">{message.content}</div>
          {message.references?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-[#f6f6f6]">
              <div className="text-[10px] uppercase tracking-wider text-[#747474] mb-1">Sources</div>
              <div className="flex flex-wrap gap-1">
                {message.references.map((r) => (
                  <span key={r.id} className="text-[10px] px-2 py-0.5 rounded-[6px] bg-[#e2e8f0] text-[#475569]">{r.title}</span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 ml-1">
          <button data-testid={`fb-up-${message.id}`} onClick={() => onFeedback(message.id, "up", null)} disabled={message.feedback === "up"} className={`w-6 h-6 rounded-[6px] flex items-center justify-center ${message.feedback === "up" ? "bg-[#b8e6cd] text-[#008932]" : "text-[#747474] hover:bg-[#f6f6f6]"}`}>
            <ThumbsUp size={11} />
          </button>
          <button data-testid={`fb-down-${message.id}`} onClick={() => setShowFB(true)} disabled={message.feedback === "down"} className={`w-6 h-6 rounded-[6px] flex items-center justify-center ${message.feedback === "down" ? "bg-[#e5c1c1] text-[#881a27]" : "text-[#747474] hover:bg-[#f6f6f6]"}`}>
            <ThumbsDown size={11} />
          </button>
          {canVerify && !message.verified && <button data-testid={`verify-${message.id}`} onClick={() => onVerify(message.id, true)} className="text-[10px] text-[#475569] hover:underline">Mark verified</button>}
          {canVerify && message.verified && <button onClick={() => onVerify(message.id, false)} className="text-[10px] text-[#881a27] hover:underline">Unverify</button>}
        </div>
        {showFB && (
          <div className="mt-2 p-3 bg-[#ffefef] rounded-[10px] border border-[#e8b4b4]">
            <textarea rows={2} placeholder={t("cop.feedback_ph")} value={fbComment} onChange={(e) => setFbComment(e.target.value)} className="sw-input !text-[12px] mb-2" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowFB(false); setFbComment(""); }} className="sw-btn-secondary !text-[11px] !px-2 !py-1">{t("common.cancel")}</button>
              <button onClick={() => { onFeedback(message.id, "down", fbComment); setShowFB(false); setFbComment(""); }} className="sw-btn-primary !text-[11px] !px-2 !py-1 !bg-[#881a27]">{t("common.submit")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Copilot() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const canVerify = ["admin", "hr"].includes(user?.role);

  const loadSessions = async () => {
    const r = await api.get("/copilot/sessions");
    setSessions(r.data);
  };
  useEffect(() => { loadSessions(); }, []);

  const openSession = async (s) => {
    setCurrentSession(s);
    const r = await api.get(`/copilot/sessions/${s.id}/messages`);
    setMessages(r.data);
  };

  const newSession = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const deleteSession = async (s) => {
    if (!window.confirm(t("cop.delete_conv"))) return;
    await api.delete(`/copilot/sessions/${s.id}`);
    if (currentSession?.id === s.id) newSession();
    loadSessions();
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    const userMsg = { id: "u-" + Date.now(), role: "user", content: text, created_at: new Date().toISOString() };
    setMessages([...messages, userMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/copilot/chat", { session_id: currentSession?.id || null, text });
      setMessages((m) => [...m, r.data.message]);
      if (!currentSession) {
        await loadSessions();
        const sessionsResp = await api.get("/copilot/sessions");
        const matched = sessionsResp.data.find((s) => s.id === r.data.session_id);
        if (matched) setCurrentSession(matched);
      }
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro");
    } finally {
      setSending(false);
    }
  };

  const feedback = async (mid, fb, comment) => {
    try {
      await api.post(`/copilot/messages/${mid}/feedback`, { feedback: fb, comment });
      setMessages((m) => m.map((x) => x.id === mid ? { ...x, feedback: fb, feedback_comment: comment } : x));
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };

  const verify = async (mid, verified) => {
    try {
      await api.post(`/copilot/messages/${mid}/verify`, { verified });
      setMessages((m) => m.map((x) => x.id === mid ? { ...x, verified } : x));
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };

  const suggestions = [
    "Quanto saldo de PTO eu tenho?",
    "Quem é o meu manager?",
    "Qual o limite de refeições em viagens?",
    "Como funciona o programa de empréstimo?",
    "Quais treinamentos eu preciso fazer?",
  ];

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-80px)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-gradient-to-br from-[#475569] to-[#334155] text-white flex items-center justify-center"><Sparkles size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">Sage <span className="text-[#475569]">· Copilot</span></h1>
            <p className="text-[14px] text-[#747474]">{t("cop.subtitle")}</p>
          </div>
        </div>
        <button data-testid="new-chat-btn" onClick={newSession} className="sw-btn-secondary"><Plus size={14} />  {t("cop.new_chat")}</button>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Sessions sidebar */}
        <div className="hidden md:flex flex-col gap-1 w-[260px] sw-card !p-3 overflow-y-auto" data-testid="copilot-sessions">
          <div className="text-[10px] uppercase tracking-wider text-[#475569] mb-2 px-2">Recent</div>
          {sessions.length === 0 ? (
            <div className="text-[12px] text-[#747474] p-2">{t("cop.no_convs")}</div>
          ) : sessions.map((s) => (
            <div key={s.id} className={`group flex items-center gap-1 p-2 rounded-[8px] cursor-pointer ${currentSession?.id === s.id ? "bg-[#e2e8f0]" : "hover:bg-[#f6f6f6]"}`}>
              <button onClick={() => openSession(s)} className="flex-1 text-left text-[12px] truncate" data-testid={`sess-${s.id}`}>{s.title}</button>
              <button onClick={() => deleteSession(s)} className="opacity-0 group-hover:opacity-100 text-[#881a27]"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col sw-card !p-0 overflow-hidden">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center max-w-[500px] mx-auto">
                <div className="w-16 h-16 rounded-[20px] bg-[#e2e8f0] flex items-center justify-center mb-3"><Sparkles size={28} className="text-[#475569]" /></div>
                <h2 className="sw-heading text-[20px] mb-1">Hi, I'm Sage</h2>
                <p className="text-[13px] text-[#747474] mb-5">Ask me anything about Sagewood — policies, processes, your benefits, or your team.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} data-testid={`suggestion-${i}`} className="text-left p-3 rounded-[12px] border border-[#eeeded] hover:border-[#475569] text-[12px] text-[#323232] bg-white">
                      <BookOpen size={12} className="inline mr-1.5 text-[#475569]" />{s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} message={m} onFeedback={feedback} onVerify={verify} canVerify={canVerify} />)
            )}
            {sending && (
              <div className="flex justify-start mb-3">
                <div className="bg-white border border-[#eeeded] rounded-[16px] rounded-tl-sm px-4 py-3 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#475569] animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-[#475569] animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-2 h-2 rounded-full bg-[#475569] animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#eeeded] p-3 flex items-end gap-2 bg-white">
            <textarea
              data-testid="copilot-input"
              rows={1}
              placeholder="Ask Sage anything…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="flex-1 sw-input !py-2 resize-none max-h-[120px]"
              disabled={sending}
            />
            <button data-testid="copilot-send-btn" onClick={send} disabled={!input.trim() || sending} className="sw-btn-primary !p-2.5"><Send size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
